import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  Modal,
  Stack,
  Text,
  Button,
  Alert,
  Group,
  Paper,
  NumberInput,
  Radio,
  Skeleton,
  Badge,
  Divider,
} from "@mantine/core";
import {
  IconCreditCard,
  IconAlertCircle,
  IconPercentage,
  IconCurrencyDollar,
} from "@tabler/icons-react";
import { useApp } from "../../context/AppContext";
import { useSalon } from "../../context/SalonContext";
import { useResponsive } from "../../context/ResponsiveContext";
import { BusinessInfo, SalonContextType } from "../../types/context";
import {
  ClientPaymentMethod,
  isCardValidForDate,
  isCardExpired,
  ChargeCardOnFileResponse,
} from "../../types/cardOnFile";
import BrandedLoadingOverlay from "../common/BrandedLoadingOverlay";
import { formatCurrency } from "../../utils/cashDrawerUtils";
import styles from "./ChargeCardOnFileModal.module.css";

interface ChargeCardOnFileModalProps {
  opened: boolean;
  onClose: () => void;
  clientId: string;
  transactionId: string;
  remainingBalance: number;
  appointmentDate?: Date;
  initialPaymentMethods?: ClientPaymentMethod[];
  onSuccess: (response: ChargeCardOnFileResponse) => void | Promise<void>;
}

type AmountType = "full" | "percentage" | "custom";

export default function ChargeCardOnFileModal({
  opened,
  onClose,
  clientId,
  transactionId,
  remainingBalance,
  appointmentDate,
  initialPaymentMethods,
  onSuccess,
}: ChargeCardOnFileModalProps) {
  const { isMobile } = useResponsive();
  const { getClientPaymentMethods, chargeCardOnFile } = useApp();
  const { businessInfo } = useSalon() as SalonContextType;
  const hasStripeConnect =
    Boolean(
      (businessInfo as BusinessInfo | null)?.stripe_connect_account_id?.trim(),
    ) &&
    Boolean(
      (businessInfo as BusinessInfo | null)
        ?.stripe_connect_account_is_onboarded,
    );

  const [paymentMethods, setPaymentMethods] = useState<ClientPaymentMethod[]>(
    [],
  );
  const [isLoadingCards, setIsLoadingCards] = useState(true);
  const [selectedCardId, setSelectedCardId] = useState<number | null>(null);
  const [amountType, setAmountType] = useState<AmountType>("full");
  const [percentage, setPercentage] = useState<number>(100);
  const [customAmount, setCustomAmount] = useState<number>(remainingBalance);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const applyPaymentMethods = useCallback((methods: ClientPaymentMethod[]) => {
    setPaymentMethods(methods);
    if (methods.length > 0) {
      const defaultCard = methods.find((m) => m.isDefault);
      setSelectedCardId(defaultCard?.id || methods[0].id);
    } else {
      setSelectedCardId(null);
    }
  }, []);

  const fetchPaymentMethods = useCallback(async () => {
    if (!clientId || !hasStripeConnect) return;

    setIsLoadingCards(true);
    setError(null);

    try {
      const response = await getClientPaymentMethods(clientId);
      const methods = response.paymentMethods || [];
      applyPaymentMethods(methods);
    } catch (err) {
      console.error("Error fetching payment methods:", err);
      setError("Failed to load saved cards");
    } finally {
      setIsLoadingCards(false);
    }
  }, [
    clientId,
    getClientPaymentMethods,
    applyPaymentMethods,
    hasStripeConnect,
  ]);

  useEffect(() => {
    if (opened) {
      if (initialPaymentMethods) {
        applyPaymentMethods(initialPaymentMethods);
        setIsLoadingCards(false);
      } else if (!hasStripeConnect) {
        applyPaymentMethods([]);
        setIsLoadingCards(false);
      } else {
        fetchPaymentMethods();
      }
      setAmountType("full");
      setPercentage(100);
      setCustomAmount(remainingBalance);
      setError(null);
    }
  }, [
    opened,
    fetchPaymentMethods,
    remainingBalance,
    initialPaymentMethods,
    applyPaymentMethods,
    hasStripeConnect,
  ]);

  const calculateChargeAmount = (): number => {
    switch (amountType) {
      case "full":
        return remainingBalance;
      case "percentage":
        return Math.round(((remainingBalance * percentage) / 100) * 100) / 100;
      case "custom":
        return customAmount;
      default:
        return remainingBalance;
    }
  };

  const chargeAmount = calculateChargeAmount();
  /** Allow charging above remaining balance (tip, overpayment, etc.). */
  const isValidAmount = chargeAmount > 0;

  const isCardValidForAppointment = (card: ClientPaymentMethod) => {
    if (!appointmentDate) return true;
    return isCardValidForDate(card, appointmentDate);
  };

  const handleCharge = async () => {
    if (!selectedCardId || !isValidAmount) return;

    setIsProcessing(true);
    setError(null);

    try {
      const response = await chargeCardOnFile({
        transactionId,
        paymentMethodId: selectedCardId,
        amountCents: Math.round(chargeAmount * 100),
      });

      try {
        await Promise.resolve(onSuccess(response));
      } catch (e) {
        console.error("Error in charge card on file success handler:", e);
      }
    } catch (err) {
      console.error("Error charging card:", err);
      setError(
        err instanceof Error
          ? err.message
          : "Failed to charge card. Please try again.",
      );
    } finally {
      setIsProcessing(false);
    }
  };

  const selectedCard = paymentMethods.find((m) => m.id === selectedCardId);
  const canCharge =
    selectedCardId &&
    isValidAmount &&
    selectedCard &&
    !isCardExpired(selectedCard);

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Group gap="xs">
          <IconCreditCard size={20} />
          <Text fw={600}>Charge Card on File</Text>
        </Group>
      }
      size="md"
      fullScreen={isMobile}
      centered
      closeOnClickOutside={!isProcessing}
      closeOnEscape={!isProcessing}
    >
      <BrandedLoadingOverlay visible={isProcessing} />

      <Stack gap="md">
        {error && (
          <Alert
            icon={<IconAlertCircle size={16} />}
            color="red"
            variant="light"
          >
            {error}
          </Alert>
        )}

        <Text size="sm" c="dimmed">
          Remaining balance:{" "}
          <Text span fw={600} c="dark">
            {formatCurrency(remainingBalance)}
          </Text>
        </Text>

        <Divider label="Select Card" labelPosition="left" />

        {isLoadingCards ? (
          <Stack gap="xs">
            <Skeleton height={60} radius="md" />
            <Skeleton height={60} radius="md" />
          </Stack>
        ) : paymentMethods.length === 0 ? (
          <Paper p="md" withBorder className={styles.emptyState}>
            <Stack align="center" gap="md">
              <IconCreditCard size={32} color="var(--mantine-color-gray-5)" />
              {hasStripeConnect ? (
                <Text size="sm" c="dimmed" ta="center">
                  No cards on file for this client
                </Text>
              ) : (
                <>
                  <Text size="sm" c="dimmed" ta="center">
                    Finish setting up your Stripe Account to start adding cards
                    to customers.
                  </Text>
                  <Button
                    component={Link}
                    to="/admin/settings/payments"
                    color="teal"
                  >
                    Payment settings
                  </Button>
                </>
              )}
            </Stack>
          </Paper>
        ) : (
          <Radio.Group
            value={selectedCardId?.toString() || ""}
            onChange={(value) => setSelectedCardId(parseInt(value))}
          >
            <Stack gap="xs">
              {paymentMethods.map((card) => {
                const expired = isCardExpired(card);
                const invalidForAppt = !isCardValidForAppointment(card);

                return (
                  <Paper
                    key={card.id}
                    p="sm"
                    withBorder
                    className={`${styles.cardOption} ${
                      selectedCardId === card.id ? styles.cardSelected : ""
                    } ${expired ? styles.cardExpired : ""}`}
                    onClick={() => !expired && setSelectedCardId(card.id)}
                    style={{ cursor: expired ? "not-allowed" : "pointer" }}
                  >
                    <Group justify="space-between" wrap="nowrap">
                      <Group gap="sm" wrap="nowrap">
                        <Radio
                          value={card.id.toString()}
                          disabled={expired}
                          color="teal"
                        />
                        <Stack gap={2}>
                          {card.nickname?.trim() ? (
                            <>
                              <Text size="sm" fw={600}>
                                {card.nickname.trim()}
                              </Text>
                              <Group gap="xs" wrap="wrap">
                                <Text size="sm" c="dimmed">
                                  {card.cardBrand} •••• {card.cardLast4}
                                </Text>
                                {card.isDefault && (
                                  <Badge size="xs" variant="light" color="teal">
                                    Default
                                  </Badge>
                                )}
                                {expired && (
                                  <Badge size="xs" variant="light" color="red">
                                    Expired
                                  </Badge>
                                )}
                                {!expired && invalidForAppt && (
                                  <Badge
                                    size="xs"
                                    variant="light"
                                    color="orange"
                                  >
                                    Expires before appointment
                                  </Badge>
                                )}
                              </Group>
                            </>
                          ) : (
                            <Group gap="xs" wrap="wrap">
                              <Text size="sm" fw={500}>
                                {card.cardBrand} •••• {card.cardLast4}
                              </Text>
                              {card.isDefault && (
                                <Badge size="xs" variant="light" color="teal">
                                  Default
                                </Badge>
                              )}
                              {expired && (
                                <Badge size="xs" variant="light" color="red">
                                  Expired
                                </Badge>
                              )}
                              {!expired && invalidForAppt && (
                                <Badge size="xs" variant="light" color="orange">
                                  Expires before appointment
                                </Badge>
                              )}
                            </Group>
                          )}
                          <Text size="xs" c="dimmed">
                            Expires{" "}
                            {card.cardExpMonth.toString().padStart(2, "0")}/
                            {card.cardExpYear.toString().slice(-2)}
                          </Text>
                        </Stack>
                      </Group>
                    </Group>
                  </Paper>
                );
              })}
            </Stack>
          </Radio.Group>
        )}

        {paymentMethods.length > 0 && (
          <>
            <Divider label="Charge Amount" labelPosition="left" />

            <Radio.Group
              value={amountType}
              onChange={(value) => setAmountType(value as AmountType)}
            >
              <Stack gap="xs">
                <Radio
                  value="full"
                  label={`Full balance (${formatCurrency(remainingBalance)})`}
                  color="teal"
                />
                <Radio
                  value="percentage"
                  label="Percentage of balance (can exceed 100%)"
                  color="teal"
                />
                {amountType === "percentage" && (
                  <Group gap="xs" ml="xl" wrap="wrap">
                    <NumberInput
                      value={percentage}
                      onChange={(val) => setPercentage(Number(val) || 0)}
                      min={0.01}
                      max={999}
                      step={5}
                      w={110}
                      rightSection={<IconPercentage size={14} />}
                    />
                    <Text size="sm" c="dimmed">
                      = {formatCurrency(calculateChargeAmount())}
                    </Text>
                  </Group>
                )}
                <Radio
                  value="custom"
                  label="Custom amount (can exceed balance)"
                  color="teal"
                />
                {amountType === "custom" && (
                  <NumberInput
                    value={customAmount}
                    onChange={(val) => setCustomAmount(Number(val) || 0)}
                    min={0.01}
                    step={0.01}
                    decimalScale={2}
                    fixedDecimalScale
                    w={150}
                    ml="xl"
                    leftSection={<IconCurrencyDollar size={14} />}
                  />
                )}
              </Stack>
            </Radio.Group>
          </>
        )}

        <Divider />

        <Group justify="space-between">
          <Text size="sm" fw={500}>
            Amount to charge:
          </Text>
          <Text size="lg" fw={700} c="teal">
            {formatCurrency(chargeAmount)}
          </Text>
        </Group>

        <Group justify="flex-end" mt="md">
          <Button variant="default" onClick={onClose} disabled={isProcessing}>
            Cancel
          </Button>
          <Button
            color="teal"
            onClick={handleCharge}
            loading={isProcessing}
            disabled={!canCharge}
            leftSection={<IconCreditCard size={18} />}
            styles={{
              root: {
                backgroundColor: "var(--brand-teal)",
                transition: "filter 120ms ease",
              },
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLButtonElement).style.filter =
                "brightness(1.08)";
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLButtonElement).style.filter = "none";
            }}
          >
            Charge Card
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
